import {TaskType} from './task';
import {SubRole, EffectiveRole, User} from './user';

export interface AccessPolicyDto {
  version: number;
  name: string;
  evaluation: AccessPolicyEvaluationDto | null;
  contextContract: AccessPolicyContextContractDto | null;
  predicates: Record<string, AccessPolicyPredicateDto>;
  actions: Record<string, string[]>;
  matrix: AccessPolicyMatrixRuleDto[];
}

export interface AccessPolicyEvaluationDto {
  strategy: string;
  defaultEffect: string;
}

export interface AccessPolicyContextContractDto {
  user: string[];
  ctx: string[];
}

export interface AccessPolicyPredicateDto {
  logic: AccessPolicyLogicNode | null;
}

export interface AccessPolicyMatrixRuleDto {
  resource: string;
  action: string;
  projectRole: string[] | null;
  boardRole: string[] | null;
  taskType: string[] | null;
  effect: string;
  condition: string | null;
}

export type AccessPolicyLogicNode =
  | null
  | string
  | number
  | boolean
  | AccessPolicyLogicNode[]
  | AccessPolicyLogicObject;

export interface AccessPolicyLogicObject {
  [key: string]: AccessPolicyLogicNode;
}

export interface AccessPolicyUserContext {
  id: number | null;
  effectiveRole: EffectiveRole | null;
  companyId: number | null;
  tradeId: number | null;
}

export interface AccessPolicyContext {
  companyId?: number | null;
  tradeId?: number | null;
  projectId?: number | null;
  projectArchived?: boolean | null;
  projectRole?: SubRole | null;
  boardId?: number | null;
  boardRole?: SubRole | null;
  authorId?: number | null;
  creatorId?: number | null;
  memberId?: number | null;
  creatorTradeId?: number | null;
  taskTradeId?: number | null;
  authorGlobalRole?: EffectiveRole | null;
  taskType?: TaskType | null;
}

export interface AccessPolicyDecision {
  allowed: boolean;
  effect: string;
  resource: string;
  action: string;
  matchedRuleIndex: number | null;
  matchedCondition: string | null;
}

export function buildAccessPolicyUserContext(user: User | null | undefined, activeCompanyId?: number | null): AccessPolicyUserContext | null {
  if (!user) {
    return null;
  }

  const companyId = (() => {
    if (!activeCompanyId) {
      return null;
    }

    if (
      user.effectiveRole === EffectiveRole.ROLE_SUPER_ADMIN
      || user.effectiveRole === EffectiveRole.ROLE_COMPANY_ADMIN
    ) {
      return activeCompanyId;
    }

    return user.companies?.some((company) => company.id === activeCompanyId)
      ? activeCompanyId
      : null;
  })();

  return {
    id: user.id ?? null,
    effectiveRole: user.effectiveRole ?? null,
    companyId,
    tradeId: user.trade?.id ?? null
  };
}
